import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";
import PersonCard from "@/components/passport/PersonCard";
import {
  getCroppedDataUrl,
  flipImageHorizontally,
  centerCropArea,
} from "@/lib/crop";
import {
  downloadPassportSheetPdf,
  downloadPassportSheetPng,
  renderPassportSheetCanvas,
  planSheet,
  sheetCapacity,
  SHEET_SIZES,
  getSheet,
} from "@/lib/passport";
import {
  ArrowLeft,
  ArrowRight,
  UploadCloud,
  RotateCw,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  FlipHorizontal,
  Undo2,
  Download,
  FileImage,
  RefreshCcw,
  AlertTriangle,
  UserPlus,
  Users,
} from "lucide-react";

const SIZE_OPTIONS = [
  {
    id: "standard",
    label: "Standard Passport",
    subtitle: "3.5 cm × 4.5 cm",
    w: 3.5,
    h: 4.5,
  },
  { id: "large", label: "Large Print", subtitle: "7.0 cm × 9.0 cm", w: 7.0, h: 9.0 },
];

let personSeq = 0;
const newPerson = (imageUrl = null) => ({
  uid: `p${++personSeq}`,
  imageUrl,
  crop: { x: 0, y: 0 },
  zoom: 1,
  rotation: 0,
  croppedAreaPixels: null,
  name: "",
  date: "",
  idNo: "",
  copies: 8,
});

export default function PassportWorkflow() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  // Sheet config
  const [photosPerRow, setPhotosPerRow] = useState(4);
  const [sizeId, setSizeId] = useState("standard");
  const [sheetId, setSheetId] = useState("a4");

  // People
  const [people, setPeople] = useState([newPerson()]);
  const [activeIdx, setActiveIdx] = useState(0);

  // Output
  const [sheetDataUrl, setSheetDataUrl] = useState(null);
  const [sheetCanvas, setSheetCanvas] = useState(null);
  const [busy, setBusy] = useState(false);

  const fileRef = useRef(null);
  const targetIdxRef = useRef(0);

  const activeSize = useMemo(
    () => SIZE_OPTIONS.find((s) => s.id === sizeId) || SIZE_OPTIONS[0],
    [sizeId],
  );
  const aspectRatio = activeSize.w / activeSize.h;
  const active = people[activeIdx];

  const readyPeople = people.filter((p) => p.imageUrl);

  const patchPerson = (idx, patch) =>
    setPeople((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    );

  const pickPhotoFor = (idx) => {
    targetIdxRef.current = idx;
    fileRef.current?.click();
  };

  const onFiles = (files) => {
    const imgs = Array.from(files || []).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (!imgs.length) {
      toast.error("Please upload JPG, PNG or WEBP images.");
      return;
    }
    const idx = targetIdxRef.current;
    const urls = imgs.map((f) => URL.createObjectURL(f));

    setPeople((prev) => {
      const next = [...prev];
      const first = next[idx];
      if (first?.imageUrl?.startsWith("blob:")) URL.revokeObjectURL(first.imageUrl);
      next[idx] = {
        ...first,
        imageUrl: urls[0],
        crop: { x: 0, y: 0 },
        zoom: 1,
        rotation: 0,
        croppedAreaPixels: null,
      };
      urls.slice(1).forEach((u) => next.push(newPerson(u)));
      return next;
    });
    setActiveIdx(idx);
    toast.success(
      urls.length > 1
        ? `${urls.length} photos loaded as separate people`
        : "Photo loaded. Adjust the crop area.",
    );
  };

  const addPerson = () => {
    setPeople((prev) => [...prev, newPerson()]);
    const idx = people.length;
    setActiveIdx(idx);
    targetIdxRef.current = idx;
    setTimeout(() => fileRef.current?.click(), 0);
  };

  const removePerson = (idx) => {
    setPeople((prev) => {
      const victim = prev[idx];
      if (victim?.imageUrl?.startsWith("blob:")) URL.revokeObjectURL(victim.imageUrl);
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [newPerson()];
    });
    setActiveIdx((i) => Math.max(0, i >= idx ? i - 1 : i));
  };

  const onCropComplete = useCallback(
    (_area, areaPixels) => {
      setPeople((prev) =>
        prev.map((p, i) =>
          i === activeIdx ? { ...p, croppedAreaPixels: areaPixels } : p,
        ),
      );
    },
    [activeIdx],
  );

  const generateSheet = async (sid = sheetId, list = people) => {
    const outputW = 700;
    const outputH = Math.round(outputW * (activeSize.h / activeSize.w));

    const entries = [];
    for (const p of list) {
      if (!p.imageUrl) continue;
      const area =
        p.croppedAreaPixels || (await centerCropArea(p.imageUrl, aspectRatio));
      const dataUrl = await getCroppedDataUrl(
        p.imageUrl,
        area,
        outputW,
        outputH,
        p.rotation,
      );
      entries.push({
        dataUrl,
        copies: p.copies,
        name: p.name,
        date: p.date,
        idNo: p.idNo,
      });
    }

    const { canvas } = await renderPassportSheetCanvas({
      people: entries,
      photoSizeCm: { w: activeSize.w, h: activeSize.h },
      photosPerRow,
      dpi: 300,
      sheetId: sid,
    });
    setSheetCanvas(canvas);
    setSheetDataUrl(canvas.toDataURL("image/png"));
  };

  const goToStep2 = async () => {
    if (!readyPeople.length) {
      toast.error("Upload at least one photo first.");
      return;
    }
    setBusy(true);
    try {
      await generateSheet();
      setStep(2);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate the sheet. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const applyLabels = async () => {
    setBusy(true);
    try {
      await generateSheet();
      toast.success("Labels updated");
    } catch (e) {
      console.error(e);
      toast.error("Could not update labels");
    } finally {
      setBusy(false);
    }
  };

  const changeSheet = async (sid) => {
    setSheetId(sid);
  };

  const flipPhoto = async () => {
    if (!active?.imageUrl) return;
    try {
      const flipped = await flipImageHorizontally(active.imageUrl);
      if (active.imageUrl.startsWith("blob:")) URL.revokeObjectURL(active.imageUrl);
      patchPerson(activeIdx, {
        imageUrl: flipped,
        crop: { x: 0, y: 0 },
        zoom: 1,
        croppedAreaPixels: null,
      });
    } catch (_e) {
      toast.error("Could not flip the photo");
    }
  };

  const resetCrop = () =>
    patchPerson(activeIdx, { crop: { x: 0, y: 0 }, zoom: 1, rotation: 0 });

  // Snap each person's copies down to what the chosen page actually holds.
  const autoFitCopies = (list) => {
    const { cols, rows } = sheetCapacity({
      sheet: getSheet(sheetId),
      photoSizeCm: { w: activeSize.w, h: activeSize.h },
      photosPerRow,
    });
    let row = 0;
    let changed = false;
    const next = list.map((p) => {
      if (!p.imageUrl) return p;
      const available = Math.max(0, rows - row) * cols;
      const copies = Math.max(1, Math.min(p.copies, available));
      row += Math.ceil(Math.min(copies, Math.max(available, 1)) / cols);
      if (copies !== p.copies) changed = true;
      return copies === p.copies ? p : { ...p, copies };
    });
    return { next, changed };
  };

  const geomKey = `${sheetId}|${sizeId}|${photosPerRow}`;
  const prevGeom = useRef(geomKey);
  useEffect(() => {
    if (prevGeom.current === geomKey) return;
    prevGeom.current = geomKey;
    const { next, changed } = autoFitCopies(people);
    if (changed) {
      setPeople(next);
      toast.info("Copies snapped down to fit this page");
    }
    if (step === 2 && next.some((p) => p.imageUrl)) {
      setBusy(true);
      generateSheet(sheetId, next)
        .catch(() => toast.error("Could not update the sheet"))
        .finally(() => setBusy(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geomKey]);

  const onDownloadPdf = async () => {
    if (!sheetCanvas) return;
    setBusy(true);
    try {
      await downloadPassportSheetPdf(
        sheetCanvas,
        `sky-basket-passport-${sheetId}.pdf`,
        sheetId,
      );
      toast.success(`PDF downloaded (${getSheet(sheetId).label})`);
    } finally {
      setBusy(false);
    }
  };

  const onDownloadPng = () => {
    if (!sheetCanvas) return;
    downloadPassportSheetPng(sheetCanvas, "sky-basket-passport-sheet.png");
    toast.success("PNG downloaded");
  };

  // Overflow is measured against the people that actually have a photo.
  const readyPlan = useMemo(
    () =>
      planSheet({
        sheet: getSheet(sheetId),
        photoSizeCm: { w: activeSize.w, h: activeSize.h },
        photosPerRow,
        people: readyPeople,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sheetId, activeSize, photosPerRow, people],
  );
  const overflow = readyPlan.overflow > 0;

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      multiple
      accept="image/jpeg,image/jpg,image/png,image/webp"
      className="hidden"
      data-testid="passport-file-input"
      onChange={(e) => {
        onFiles(e.target.files);
        e.target.value = "";
      }}
    />
  );

  return (
    <AdminShell breadcrumbs={[{ label: "Passport Photo Creator" }]} step={step}>
      {fileInput}
      {step === 1 ? (
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#cb05a5]">
                Step 1 · Configure
              </div>
              <h1 className="mt-2 font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight">
                Passport Photo Creator
              </h1>
              <p className="mt-2 text-slate-600 max-w-2xl">
                Add one or more people — each keeps its own photo, crop, labels
                and copy count, and gets its own block of rows on a single sheet.
              </p>
            </div>
            <button
              type="button"
              onClick={() => nav("/admin")}
              data-testid="passport-back-dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-slate-600 hover:text-slate-900 hover:bg-white transition-colors"
            >
              <ArrowLeft size={16} /> Back to dashboard
            </button>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Card title="Paper size">
                <div className="grid grid-cols-2 gap-3">
                  {SHEET_SIZES.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSheetId(s.id)}
                      data-testid={`passport-sheet-${s.id}`}
                      className={`text-left p-4 rounded-2xl border-2 transition-all ${
                        sheetId === s.id
                          ? "border-[#299fe0] bg-[#299fe0]/5"
                          : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                    >
                      <div className="font-semibold text-slate-900">{s.label}</div>
                      <div className="mt-1 text-sm text-slate-500">{s.subtitle}</div>
                    </button>
                  ))}
                </div>
              </Card>

              <Card title="Photo size">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SIZE_OPTIONS.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSizeId(s.id)}
                      data-testid={`passport-size-${s.id}`}
                      className={`text-left p-4 rounded-2xl border-2 transition-all ${
                        sizeId === s.id
                          ? "border-[#299fe0] bg-[#299fe0]/5"
                          : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                    >
                      <div className="font-semibold text-slate-900">{s.label}</div>
                      <div className="mt-1 text-sm text-slate-500">{s.subtitle}</div>
                    </button>
                  ))}
                </div>
              </Card>

              <Card title="Photos per row">
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={photosPerRow}
                    data-testid="passport-photos-per-row"
                    onChange={(e) =>
                      setPhotosPerRow(
                        Math.max(1, Math.min(8, Number(e.target.value) || 1)),
                      )
                    }
                    className="w-24 px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-[#299fe0] focus:ring-4 focus:ring-[#299fe0]/15 outline-none font-medium"
                  />
                  <div className="text-sm text-slate-500">
                    Sheet grid: {readyPlan.cols} per row × {readyPlan.rows} rows ={" "}
                    {readyPlan.capacity} photos on one {getSheet(sheetId).label} page
                  </div>
                </div>
              </Card>

              <div
                data-testid="people-section"
                className="rounded-2xl bg-white sb-hairline sb-shadow p-5"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-slate-500">
                    <Users size={13} className="text-[#cb05a5]" />
                    People on this sheet ({people.length})
                  </div>
                  <button
                    type="button"
                    onClick={addPerson}
                    data-testid="add-person-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-sm font-semibold transition-colors"
                  >
                    <UserPlus size={14} /> Add person
                  </button>
                </div>

                <div className="space-y-3">
                  {people.map((p, i) => (
                    <PersonCard
                      key={p.uid}
                      person={p}
                      index={i}
                      active={i === activeIdx}
                      canRemove={people.length > 1}
                      onSelect={() => setActiveIdx(i)}
                      onChange={(patch) => patchPerson(i, patch)}
                      onRemove={() => removePerson(i)}
                      onPickPhoto={() => pickPhotoFor(i)}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  data-testid="passport-dropzone"
                  onClick={() => pickPhotoFor(activeIdx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    targetIdxRef.current = activeIdx;
                    onFiles(e.dataTransfer.files);
                  }}
                  className="mt-4 w-full cursor-pointer border-2 border-dashed border-slate-200 hover:border-[#299fe0] rounded-2xl p-6 flex flex-col items-center justify-center text-center transition-colors"
                >
                  <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center text-white"
                    style={{ background: "linear-gradient(135deg,#299fe0,#cb05a5)" }}
                  >
                    <UploadCloud size={20} />
                  </div>
                  <div className="mt-3 font-semibold text-slate-800">
                    Drop photos or click to browse
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Select several at once — each photo becomes its own person
                  </div>
                </button>
              </div>

              {overflow && (
                <div
                  data-testid="passport-overflow-warning"
                  className="rounded-2xl border-2 border-[#f59e0b] bg-[#fffbeb] p-4 flex gap-3"
                >
                  <AlertTriangle size={18} className="text-[#b45309] shrink-0 mt-0.5" />
                  <div className="text-sm text-[#78350f]">
                    <span className="font-bold">
                      {readyPlan.overflow} of {readyPlan.requested} copies don't fit
                    </span>{" "}
                    on one {getSheet(sheetId).label} page at {activeSize.subtitle} (
                    {readyPlan.cols} per row × {readyPlan.rows} row
                    {readyPlan.rows === 1 ? "" : "s"}, and each person starts on a
                    fresh row). Reduce copies, remove a person, pick the smaller
                    photo size, or switch paper size.
                  </div>
                </div>
              )}

              {!!readyPeople.length && (
                <>
                  <Card title={`Crop tools · Person ${activeIdx + 1}`}>
                    {active?.imageUrl ? (
                      <>
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min={1}
                            max={4}
                            step={0.01}
                            value={active.zoom}
                            onChange={(e) =>
                              patchPerson(activeIdx, { zoom: Number(e.target.value) })
                            }
                            data-testid="passport-zoom-slider"
                            className="flex-1 accent-[#299fe0]"
                          />
                          <span className="text-xs font-semibold text-slate-500 w-10 text-right">
                            {active.zoom.toFixed(1)}×
                          </span>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <IconTool
                            testid="crop-zoom-out"
                            label="Zoom out"
                            icon={ZoomOut}
                            onClick={() =>
                              patchPerson(activeIdx, {
                                zoom: Math.max(1, +(active.zoom - 0.2).toFixed(2)),
                              })
                            }
                          />
                          <IconTool
                            testid="crop-zoom-in"
                            label="Zoom in"
                            icon={ZoomIn}
                            onClick={() =>
                              patchPerson(activeIdx, {
                                zoom: Math.min(4, +(active.zoom + 0.2).toFixed(2)),
                              })
                            }
                          />
                          <IconTool
                            testid="crop-rotate-left"
                            label="Rotate left"
                            icon={RotateCcw}
                            onClick={() =>
                              patchPerson(activeIdx, {
                                rotation: (active.rotation + 270) % 360,
                              })
                            }
                          />
                          <IconTool
                            testid="passport-rotate-btn"
                            label="Rotate right"
                            icon={RotateCw}
                            onClick={() =>
                              patchPerson(activeIdx, {
                                rotation: (active.rotation + 90) % 360,
                              })
                            }
                          />
                          <IconTool
                            testid="crop-flip"
                            label="Flip"
                            icon={FlipHorizontal}
                            onClick={flipPhoto}
                          />
                          <IconTool
                            testid="crop-reset"
                            label="Reset"
                            icon={Undo2}
                            onClick={resetCrop}
                          />
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">
                        Upload a photo for person {activeIdx + 1} to crop it.
                      </p>
                    )}
                  </Card>

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={goToStep2}
                      disabled={busy}
                      data-testid="generate-passport-btn"
                      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-white font-semibold shadow-lg disabled:opacity-60 hover:-translate-y-0.5 transition-transform"
                      style={{
                        background: "linear-gradient(135deg,#299fe0,#cb05a5 140%)",
                      }}
                    >
                      {busy ? "Generating…" : "Generate Passport Sheet"}
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Right: cropper for the selected person */}
            <div className="lg:sticky lg:top-32 h-fit">
              <div className="rounded-3xl bg-white sb-hairline sb-shadow p-4 md:p-6">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-500">
                    Crop preview · Person {activeIdx + 1}
                  </div>
                  <div className="text-xs text-slate-500">{activeSize.subtitle}</div>
                </div>
                <div
                  className="relative w-full aspect-[3.5/4.5] bg-slate-900 rounded-2xl overflow-hidden"
                  data-testid="passport-cropper"
                >
                  {active?.imageUrl ? (
                    <Cropper
                      key={active.uid}
                      image={active.imageUrl}
                      crop={active.crop}
                      zoom={active.zoom}
                      rotation={active.rotation}
                      aspect={aspectRatio}
                      onCropChange={(c) => patchPerson(activeIdx, { crop: c })}
                      onZoomChange={(z) => patchPerson(activeIdx, { zoom: z })}
                      onCropComplete={onCropComplete}
                      objectFit="contain"
                      showGrid={true}
                      style={{ containerStyle: { background: "#0f172a" } }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">
                      Upload a photo to start cropping
                    </div>
                  )}
                </div>
                <div className="mt-3 text-xs text-slate-500 text-center">
                  Drag to reposition · pinch/scroll or use the slider to zoom
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-6xl px-6 md:px-8 py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#cb05a5]">
                Step 2 · Preview &amp; Export
              </div>
              <h1 className="mt-2 font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight">
                Your print-ready passport sheet
              </h1>
              <p className="mt-2 text-slate-600 max-w-2xl">
                Verify every block, then download as PDF or PNG. Print at 100%
                scale on {getSheet(sheetId).label} — cutting guides are included.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div
                data-testid="passport-paper-size-group"
                className="flex items-center gap-1 p-1 rounded-full bg-white sb-hairline"
              >
                {SHEET_SIZES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => changeSheet(s.id)}
                    disabled={busy}
                    data-testid={`passport-paper-${s.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                      sheetId === s.id
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                data-testid="passport-regenerate-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 text-slate-700 hover:border-slate-900 font-semibold transition-colors"
              >
                <RefreshCcw size={14} /> Re-configure
              </button>
              <button
                type="button"
                onClick={onDownloadPng}
                data-testid="download-passport-png-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold transition-colors"
              >
                <FileImage size={14} /> Download PNG
              </button>
              <button
                type="button"
                onClick={onDownloadPdf}
                disabled={busy}
                data-testid="download-passport-pdf-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold shadow disabled:opacity-60"
                style={{ background: "linear-gradient(135deg,#299fe0,#cb05a5 140%)" }}
              >
                <Download size={16} />
                {busy ? "Exporting…" : `Download PDF · ${getSheet(sheetId).label}`}
              </button>
            </div>
          </div>

          <div
            data-testid="step2-labels"
            className="mt-8 mx-auto max-w-[760px] rounded-2xl bg-white sb-hairline sb-shadow p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-slate-500">
                <Users size={13} className="text-[#cb05a5]" />
                Labels per person
              </div>
              <button
                type="button"
                onClick={applyLabels}
                disabled={busy}
                data-testid="passport-apply-labels-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-900 bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors disabled:opacity-60"
              >
                <RefreshCcw size={14} /> Apply labels
              </button>
            </div>

            <div className="space-y-3">
              {people.map((p, i) =>
                p.imageUrl ? (
                  <div
                    key={p.uid}
                    data-testid={`step2-person-${i}`}
                    className="grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr_1fr] gap-3 items-end"
                  >
                    <div className="flex items-center gap-2 pb-2.5">
                      <img
                        src={p.imageUrl}
                        alt={`Person ${i + 1}`}
                        className="w-9 h-12 rounded-lg object-cover border border-slate-200"
                      />
                      <span className="text-xs font-semibold text-slate-500">
                        ×{p.copies}
                      </span>
                    </div>
                    <LabelInput
                      label="Name"
                      testid={`passport-step2-name-${i}`}
                      value={p.name}
                      placeholder="Sachin Taman"
                      onChange={(v) => patchPerson(i, { name: v })}
                    />
                    <LabelInput
                      label="DOB"
                      testid={`passport-step2-date-${i}`}
                      value={p.date}
                      placeholder="01/01/1999"
                      onChange={(v) => patchPerson(i, { date: v })}
                    />
                    <LabelInput
                      label="ID"
                      testid={`passport-step2-id-${i}`}
                      value={p.idNo}
                      placeholder="123131XXXXX989XX"
                      onChange={(v) => patchPerson(i, { idNo: v })}
                    />
                  </div>
                ) : null,
              )}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Leave a field blank to skip that line — nothing is filled in for you.
            </p>
          </div>

          <div className="mt-6 mx-auto max-w-[720px] rounded-3xl bg-white sb-shadow overflow-hidden">
            {sheetDataUrl ? (
              <img
                src={sheetDataUrl}
                alt="Passport photo sheet"
                data-testid="passport-sheet-preview"
                className="w-full h-auto"
              />
            ) : (
              <div className="aspect-[1/1.414] flex items-center justify-center text-slate-500">
                Generating…
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-slate-500">
            Sheet: {getSheet(sheetId).label} ({getSheet(sheetId).subtitle}) · Photos:{" "}
            {activeSize.subtitle} · People: {readyPeople.length} · Printed:{" "}
            {readyPlan.placedTotal} of {readyPlan.requested} copies ·{" "}
            {readyPlan.cols} per row
          </div>

          {overflow && (
            <div
              data-testid="passport-overflow-warning-step2"
              className="mt-4 mx-auto max-w-[720px] rounded-2xl border-2 border-[#f59e0b] bg-[#fffbeb] p-4 flex gap-3"
            >
              <AlertTriangle size={18} className="text-[#b45309] shrink-0 mt-0.5" />
              <div className="text-sm text-[#78350f]">
                <span className="font-bold">
                  {readyPlan.overflow} of {readyPlan.requested} copies don't fit
                </span>{" "}
                on one {getSheet(sheetId).label} page — only {readyPlan.placedTotal}{" "}
                were placed. Reduce copies or switch paper size.
              </div>
            </div>
          )}
        </div>
      )}
    </AdminShell>
  );
}

function LabelInput({ label, testid, value, placeholder, onChange }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-1">
        {label}
      </label>
      <input
        type="text"
        value={value}
        data-testid={testid}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2.5 py-2 rounded-lg bg-white border border-slate-200 focus:border-[#299fe0] focus:ring-4 focus:ring-[#299fe0]/15 outline-none text-sm font-medium"
      />
    </div>
  );
}

function IconTool({ testid, label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={label}
      aria-label={label}
      className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-slate-900 hover:bg-slate-900 hover:text-white text-slate-700 text-sm font-semibold transition-colors"
    >
      <Icon size={16} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl bg-white sb-hairline sb-shadow p-5">
      <div className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-500 mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}
